import React, { useState, useRef } from "react";
import { Download, Mail, MessageCircle, Upload, X, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { API_URL } from "../../constants";
import { axios } from "../../constants";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const ShareModal = ({ isOpen, onClose, previewRef, watermarkEnabled }) => {
  const [activeTab, setActiveTab] = useState("download");
  const [contacts, setContacts] = useState([]);
  const [manualContact, setManualContact] = useState({ name: "", email: "", phone: "" });
  const [emailSubject, setEmailSubject] = useState("You're Invited!");
  const [emailMessage, setEmailMessage] = useState("I'd love for you to join us for a special celebration!");
  const [waMessage, setWaMessage] = useState("Hi! You're invited to a special celebration. Check your email for the invitation card!");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const captureImage = async () => {
    if (!previewRef?.current) return null;
    const canvas = await html2canvas(previewRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: null,
      logging: false,
    });
    return canvas;
  };

  const handleDownloadPNG = async () => {
    setLoading(true);
    try {
      const canvas = await captureImage();
      if (!canvas) return;
      const link = document.createElement("a");
      link.download = "invitation.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
      setResult({ type: "success", message: "Invitation downloaded as PNG!" });
    } catch (err) {
      setResult({ type: "error", message: "Failed to download. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadJPG = async () => {
    setLoading(true);
    try {
      const canvas = await captureImage();
      if (!canvas) return;
      const link = document.createElement("a");
      link.download = "invitation.jpg";
      link.href = canvas.toDataURL("image/jpeg", 0.95);
      link.click();
      setResult({ type: "success", message: "Invitation downloaded as JPG!" });
    } catch (err) {
      setResult({ type: "error", message: "Failed to download. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setLoading(true);
    try {
      const canvas = await captureImage();
      if (!canvas) return;
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "px", format: [canvas.width, canvas.height] });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save("invitation.pdf");
      setResult({ type: "success", message: "Invitation downloaded as PDF!" });
    } catch (err) {
      setResult({ type: "error", message: "Failed to download. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API_URL}/api/invitations/upload-contacts`, formData);
      setContacts((prev) => {
        const existing = new Set(prev.map((c) => c.email + c.phone));
        const newContacts = res.data.contacts.filter((c) => !existing.has(c.email + c.phone));
        return [...prev, ...newContacts];
      });
      setResult({ type: "success", message: `Loaded ${res.data.count} contacts from file` });
    } catch (err) {
      setResult({ type: "error", message: err.response?.data?.detail || "Failed to parse contacts file" });
    } finally {
      setLoading(false);
    }
  };

  const addManualContact = () => {
    if (!manualContact.name && !manualContact.email && !manualContact.phone) return;
    setContacts((prev) => [...prev, { ...manualContact }]);
    setManualContact({ name: "", email: "", phone: "" });
  };

  const removeContact = (idx) => {
    setContacts((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleShareEmail = async () => {
    const emailContacts = contacts.filter((c) => c.email);
    if (emailContacts.length === 0) {
      setResult({ type: "error", message: "Add at least one contact with an email address" });
      return;
    }
    setLoading(true);
    try {
      const canvas = await captureImage();
      if (!canvas) return;
      const imageData = canvas.toDataURL("image/png");
      await axios.post(`${API_URL}/api/invitations/share-email`, {
        recipients: emailContacts,
        subject: emailSubject,
        message: emailMessage,
        image_data: imageData,
      });
      setResult({ type: "success", message: `Sending invitations to ${emailContacts.length} email(s)!` });
    } catch (err) {
      setResult({ type: "error", message: err.response?.data?.detail || "Failed to send emails" });
    } finally {
      setLoading(false);
    }
  };

  const handleShareWhatsApp = async () => {
    const phoneContacts = contacts.filter((c) => c.phone);
    if (phoneContacts.length === 0) {
      setResult({ type: "error", message: "Add at least one contact with a phone number" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/invitations/share-whatsapp`, {
        recipients: phoneContacts,
        message: waMessage,
      });
      setResult({ type: "success", message: `Sending WhatsApp invitations to ${phoneContacts.length} contact(s)!` });
    } catch (err) {
      setResult({ type: "error", message: err.response?.data?.detail || "Failed to send WhatsApp messages" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="share-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#E8E8E8]">
          <h3 className="text-lg font-bold text-[#1E1B4B]">Download & Share</h3>
          <button onClick={onClose} className="p-1 hover:bg-[#F1F5F9] rounded-full transition-colors" data-testid="close-share-modal">
            <X size={20} className="text-[#475569]" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#E8E8E8]">
          {[
            { id: "download", icon: Download, label: "Download" },
            { id: "email", icon: Mail, label: "Email" },
            { id: "whatsapp", icon: MessageCircle, label: "WhatsApp" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => { setActiveTab(t.id); setResult(null); }}
              data-testid={`share-tab-${t.id}`}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.id
                  ? "border-[#E83E8C] text-[#E83E8C]"
                  : "border-transparent text-[#475569] hover:text-[#1E1B4B]"
              }`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Result message */}
          {result && (
            <div
              className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                result.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
              }`}
              data-testid="share-result"
            >
              {result.type === "success" ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              {result.message}
            </div>
          )}

          {activeTab === "download" && (
            <div className="space-y-3">
              {watermarkEnabled && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                  Watermark included. Verify your booking to remove it!
                </p>
              )}
              <button onClick={handleDownloadPNG} disabled={loading} data-testid="download-png-btn"
                className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />} Download PNG
              </button>
              <button onClick={handleDownloadJPG} disabled={loading} data-testid="download-jpg-btn"
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-[#E83E8C] text-[#E83E8C] rounded-xl font-semibold hover:bg-[#E83E8C]/5 transition-colors disabled:opacity-50">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />} Download JPG
              </button>
              <button onClick={handleDownloadPDF} disabled={loading} data-testid="download-pdf-btn"
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-[#1E1B4B] text-[#1E1B4B] rounded-xl font-semibold hover:bg-[#1E1B4B]/5 transition-colors disabled:opacity-50">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />} Download PDF
              </button>
            </div>
          )}

          {(activeTab === "email" || activeTab === "whatsapp") && (
            <>
              {/* Contact management */}
              <ContactManager
                contacts={contacts}
                manualContact={manualContact}
                setManualContact={setManualContact}
                addManualContact={addManualContact}
                removeContact={removeContact}
                fileInputRef={fileInputRef}
                handleFileUpload={handleFileUpload}
                filterType={activeTab === "email" ? "email" : "phone"}
              />

              {activeTab === "email" && (
                <div className="space-y-3 pt-2 border-t border-[#E8E8E8]">
                  <div>
                    <label className="block text-xs font-semibold text-[#475569] mb-1 uppercase tracking-wider">Subject</label>
                    <input type="text" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)}
                      className="w-full px-3 py-2 border border-[#E8E8E8] rounded-lg text-sm focus:ring-2 focus:ring-[#E83E8C]/20 focus:border-[#E83E8C] outline-none" data-testid="email-subject-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#475569] mb-1 uppercase tracking-wider">Message</label>
                    <textarea value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} rows={3}
                      className="w-full px-3 py-2 border border-[#E8E8E8] rounded-lg text-sm focus:ring-2 focus:ring-[#E83E8C]/20 focus:border-[#E83E8C] outline-none resize-none" data-testid="email-message-input" />
                  </div>
                  <button onClick={handleShareEmail} disabled={loading} data-testid="send-email-btn"
                    className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <Mail size={18} />}
                    Send to {contacts.filter((c) => c.email).length} Email(s)
                  </button>
                </div>
              )}

              {activeTab === "whatsapp" && (
                <div className="space-y-3 pt-2 border-t border-[#E8E8E8]">
                  <div>
                    <label className="block text-xs font-semibold text-[#475569] mb-1 uppercase tracking-wider">WhatsApp Message</label>
                    <textarea value={waMessage} onChange={(e) => setWaMessage(e.target.value)} rows={3}
                      className="w-full px-3 py-2 border border-[#E8E8E8] rounded-lg text-sm focus:ring-2 focus:ring-[#E83E8C]/20 focus:border-[#E83E8C] outline-none resize-none" data-testid="wa-message-input" />
                  </div>
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                    WhatsApp sharing works via Twilio. Recipients must have joined the DoozyGo sandbox to receive messages.
                  </p>
                  <button onClick={handleShareWhatsApp} disabled={loading} data-testid="send-whatsapp-btn"
                    className="w-full flex items-center justify-center gap-2 py-3 bg-[#25D366] text-white rounded-xl font-semibold hover:bg-[#1da851] transition-colors disabled:opacity-50">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <MessageCircle size={18} />}
                    Send to {contacts.filter((c) => c.phone).length} Contact(s)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ContactManager = ({ contacts, manualContact, setManualContact, addManualContact, removeContact, fileInputRef, handleFileUpload, filterType }) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} className="hidden" />
        <button
          onClick={() => fileInputRef.current?.click()}
          data-testid="upload-contacts-btn"
          className="flex items-center gap-1.5 px-3 py-2 border border-[#E83E8C] text-[#E83E8C] rounded-lg text-sm font-medium hover:bg-[#E83E8C]/5 transition-colors"
        >
          <Upload size={14} /> Upload CSV/Excel
        </button>
        <span className="text-xs text-[#475569]">Columns: name, email, phone</span>
      </div>

      {/* Manual add */}
      <div className="flex gap-2 flex-wrap">
        <input
          data-testid="contact-name-input"
          type="text"
          placeholder="Name"
          value={manualContact.name}
          onChange={(e) => setManualContact({ ...manualContact, name: e.target.value })}
          className="flex-1 min-w-[80px] px-2 py-1.5 border border-[#E8E8E8] rounded-lg text-sm outline-none"
        />
        {filterType === "email" ? (
          <input
            data-testid="contact-email-input"
            type="email"
            placeholder="Email"
            value={manualContact.email}
            onChange={(e) => setManualContact({ ...manualContact, email: e.target.value })}
            className="flex-1 min-w-[120px] px-2 py-1.5 border border-[#E8E8E8] rounded-lg text-sm outline-none"
          />
        ) : (
          <input
            data-testid="contact-phone-input"
            type="tel"
            placeholder="Phone"
            value={manualContact.phone}
            onChange={(e) => setManualContact({ ...manualContact, phone: e.target.value })}
            className="flex-1 min-w-[100px] px-2 py-1.5 border border-[#E8E8E8] rounded-lg text-sm outline-none"
          />
        )}
        <button onClick={addManualContact} data-testid="add-contact-btn"
          className="px-3 py-1.5 bg-[#E83E8C] text-white rounded-lg text-sm font-medium hover:bg-[#D63384] transition-colors">
          Add
        </button>
      </div>

      {/* Contact list */}
      {contacts.length > 0 && (
        <div className="max-h-32 overflow-y-auto space-y-1">
          {contacts.map((c, idx) => (
            <div key={c.email || c.phone || `contact-${idx}`} className="flex items-center justify-between bg-[#F8FAFC] px-3 py-1.5 rounded-lg text-sm">
              <span className="text-[#1E1B4B] truncate">
                {c.name || "No name"} — {filterType === "email" ? c.email : c.phone}
              </span>
              <button onClick={() => removeContact(idx)} className="text-[#94a3b8] hover:text-red-500 ml-2">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
